import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { reverse } from 'ramda'
import { breakpoints, colors } from '@/styles'
import config from '@/config'
import { createModal, useModalize } from '@/components/modalize/mui'
import { Button } from '@/components/button'
import { H4, ParagraphRegular } from '@/components/text'
import image from './payment-incomplete.png'

const { WEB_URL } = config

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
`

const ImageWrapper = styled.div`
  width: 100%;
  background-color: ${colors.red96};
  display: flex;
  justify-content: center;
  padding: 52px 24px 16px;

  img {
    height: 164px;
    width: auto;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    img {
      height: auto;
      width: 100%;
      max-width: 350px;
    }
  }
`

const ContentWrapper = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const Description = styled(ParagraphRegular)`
  color: ${colors.grey56};
  margin: 8px 0 24px;
`

const ButtonsWrapper = styled.div`
  display: flex;
  width: 100%;

  button,
  a {
    width: 50%;
  }

  > :first-child {
    margin-right: 16px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column-reverse;

    button,
    a {
      width: 100%;
    }

    > :first-child {
      margin: 16px 0 0;
    }
  }
`

const orderButton = (arr, flip) => (flip ? reverse([...arr]) : arr)

const PaymentIncompleteModal = ({ intendedPlan }) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()

  const plan = intendedPlan?.plan
  const query = plan ? `?accountType=${plan}` : ''
  // For the case of essentials, we show different
  // text to confirm that they plan to use essentials.
  // Otherwise we show the continue payment text.
  const continuePayment = plan !== 'essentials'

  const removeIntendedPlanFlag = async () => {
    const response = await dispatch.session.deleteUserFlag(intendedPlan.id)
    if (response?.ok) {
      close()
    }
  }

  return (
    <Wrapper>
      <ImageWrapper>
        <img src={image} alt="" />
      </ImageWrapper>
      <ContentWrapper>
        <H4>
          {continuePayment
            ? t('l.profile.view.continue_payment.title', `Let's continue where we left off`)
            : t('l.profile.view.continue_essentials.title', 'Are you sure?')}
        </H4>
        <Description>
          {continuePayment
            ? t(
                'l.profile.view.continue_payment.description',
                `Unlock access to more rental management features - like credit checks and automated rent collection. If you're not ready yet, you can still post unlimited listings and enjoy basic management features with our Essentials plan and upgrade whenever you want.`
              )
            : t(
                'l.profile.view.continue_essentials.description',
                `Would you still like to downgrade to the Essentials plan? You'll be missing out on more included credit checks per month, automated rent collection, lease addendums & more. Don't worry – you can still post unlimited listings and upgrade your plan at any time.`
              )}
        </Description>
        <ButtonsWrapper>
          {orderButton(
            [
              <Button
                key="downgrade-plan"
                theme={continuePayment ? 'text' : 'primary'}
                onClick={removeIntendedPlanFlag}
                id={`landlord-register-${plan}-downgrade-essentials-click`}
              >
                {t('l.profile.view.remove_flag.button', 'Not right now')}
              </Button>,
              <Button
                key="upgrade-plan"
                theme={continuePayment ? 'primary' : 'text'}
                href={`${WEB_URL}/${continuePayment ? 'register' + query : 'pricing'}`}
                id={`landlord-register-${plan}-continue-to-payment-click`}
              >
                {continuePayment
                  ? t('l.profile.view.continue_to_payment.button', 'Continue to Payment')
                  : t('l.profile.view.continue_essentials.button', 'Resume My Upgrade')}
              </Button>
            ],
            !continuePayment
          )}
        </ButtonsWrapper>
      </ContentWrapper>
    </Wrapper>
  )
}

export const showPaymentIncompleteModal = createModal(PaymentIncompleteModal, {
  width: '100%',
  maxWidth: '553px',
  clickOutside: false,
  showClose: false
})
